import { useDarkMode } from '@/contexts/DarkModeContext';
import Alert from 'antd/es/alert';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Typography from 'antd/es/typography';
import React, { useCallback, useState } from 'react';
import { styled } from 'styled-components';
import { JsonEditor } from 'ui-components';

import type { OutputBlockConfig, PipelineBlock } from '@mai/types';

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginXS}px;
`;

const EditorLabel = styled(Typography.Text)`
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.marginXXS}px;
`;

const EditorHelp = styled(Typography.Text)`
  color: ${({ theme }) => theme.colorTextSecondary};
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  margin-bottom: ${({ theme }) => theme.marginXS}px;
`;

interface OutputBlockEditorProps {
  block: PipelineBlock & { type: 'output' };
  onConfigChange: (config: OutputBlockConfig) => void;
}

export const OutputBlockEditor: React.FC<OutputBlockEditorProps> = ({
  block,
  onConfigChange,
}) => {
  const [form] = Form.useForm();
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isDarkMode] = useDarkMode();

  // Initialize form with current values
  React.useEffect(() => {
    form.setFieldsValue({
      name: block.config.name || 'output',
    });
  }, [block.config, form]);

  // Convert the current JSON schema to a string for the editor with prettification
  const currentSchemaString = React.useMemo(() => {
    const schema = block.config.json_schema;

    // If no schema exists, return empty object
    if (!schema) {
      return '{}';
    }

    try {
      let jsonToStringify;

      // If it's already a string, try to parse it first
      if (typeof schema === 'string') {
        if ((schema as string).trim() === '') {
          return '{}';
        }
        jsonToStringify = JSON.parse(schema);
      } else {
        // It's already an object
        jsonToStringify = schema;
      }

      // If it's an empty object, return formatted empty object
      if (
        typeof jsonToStringify === 'object' &&
        Object.keys(jsonToStringify).length === 0
      ) {
        return '{}';
      }

      // Prettify the JSON
      return JSON.stringify(jsonToStringify, null, 2);
    } catch {
      // If parsing fails, return the original value if it's a string, otherwise empty object
      if (typeof schema === 'string') {
        return schema;
      }
      return '{}';
    }
  }, [block.config.json_schema]);

  const handleFormChange = (_: unknown, allValues: { name?: string }) => {
    onConfigChange({
      ...block.config,
      name: allValues.name || 'output',
    });
  };

  const handleJsonChange = useCallback(
    (value: string) => {
      // Only update config when JSON is valid
      if (isValid) {
        try {
          const parsedSchema = JSON.parse(value);
          onConfigChange({
            ...block.config,
            json_schema: parsedSchema,
          });
        } catch {
          // Invalid JSON - don't update config
        }
      }
    },
    [block.config, onConfigChange, isValid],
  );

  const handleValidation = useCallback(
    (valid: boolean, errorMessage?: string) => {
      setIsValid(valid);
      setError(errorMessage || null);
    },
    [],
  );

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        size="small"
        onValuesChange={handleFormChange}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Name is required' }]}
          help="The name for this output in the pipeline execution results"
        >
          <Input placeholder="output" />
        </Form.Item>
      </Form>

      <EditorContainer>
        <EditorLabel>JSON Schema (Optional)</EditorLabel>
        <EditorHelp>
          Optional JSON schema to validate the output data
        </EditorHelp>

        {error && (
          <Alert
            type="error"
            message="Invalid JSON"
            description={error}
            showIcon
          />
        )}

        <JsonEditor
          value={currentSchemaString}
          onChange={handleJsonChange}
          onValidate={handleValidation}
          height="200px"
          placeholder='{"type": "object", "properties": {"result": {"type": "string"}}}'
          theme={isDarkMode ? 'dark' : 'light'}
        />
      </EditorContainer>
    </>
  );
};
